import { affiliateApi, brandApi } from "../lib/axios";
import { Affiliate, AffiliateEnrollment } from "../types/affiliate";
import { Program } from "../types/program";
import type { MatchingPaymentInfo } from "./payout.service";
import type { ProgramsAnalytics } from "@/types/programs-analytics";

interface CreateProgramPayload {
  name: string;
  description: string;
  commissionValue: number;
}

export interface ProgramDetails {
  username: string;
  id: string;
  companyName: string;
  website: string;
  description: string;
  sessionId: string;
  brandBannerImage: string;
  brandBannerImageMobile?: string | null;
  brandProfileImage: string;
  program: Program;
  affiliateEnrollment: AffiliateEnrollment;
  shop: string;
  shortDiscountUrl?: boolean;
  attributionMethod?: 'STATIC_CODE' | 'SNEAKYLINK';
  sneakylink?: {
    token: string;
    url: string;
    // Brand-domain landing URL for customers — wrapper redirect (`/<code>`)
    // when set, falling back to `/?rs_token=<token>`. Affiliates share this.
    storeUrl?: string;
    clickCount: number;
    conversionCount: number;
  };
}

/** Renewal commission settings on a program (Recharge subscriptions). */
export interface ProgramRenewalSettings {
  renewalsEnabled: boolean;
  renewalRateType: "PERCENTAGE" | "FIXED";
  renewalRate: number;
  /** Paid renewals per subscription; null = forever. */
  renewalPaidCap: number | null;
}

/** One rung on a brand's program ladder. */
export interface ProgramTier {
  id?: string;
  name: string;
  /** Gross sales needed to reach this tier. */
  threshold: number;
  /** Commission at this tier, in the program's commissionType. */
  commissionValue: number;
  perks?: string | null;
}

/** GET /program/affiliate/hub/:programId */
export interface AffiliateProgramHub {
  brand: {
    companyName: string | null;
    brandProfileImage: string | null;
    website: string | null;
  };
  program: {
    id: string;
    name: string | null;
    commissionType: "PERCENTAGE" | "FIXED";
    commissionValue: number;
    discountValue: number;
    attributionMethod: "STATIC_CODE" | "SNEAKYLINK";
    sneakylinkCodeLifetime: number | null;
    automaticApproval: boolean;
  };
  earnings: {
    allTime: number;
    thisMonth: number;
    available: number;
    requested: number;
    paidOut: number;
  };
  standing: {
    totalSales: number;
    totalOrders: number;
    pendingCommission: number;
    allTimeCommission: number;
    lastOrderDate: string | null;
    enrolledAt: string;
    effectiveCommissionValue: number;
    currentTier: ProgramTier | null;
    nextTier: ProgramTier | null;
    progress: number;
    remainingToNext: number;
  };
  tiers: ProgramTier[];
  performance: {
    windowDays: number;
    current: { sales: number; orders: number; commission: number };
    previous: { sales: number; orders: number; commission: number };
    salesByDay: { date: string; revenue: number }[];
    clicks: number | null;
    conversions: number | null;
  };
  /** The affiliate's own Recharge subscriptions with this brand. Null when
   *  the brand has not connected Recharge. */
  recurring: {
    enabled: boolean;
    rateType: "PERCENTAGE" | "FIXED";
    rate: number;
    /** Paid renewals per subscription; null = every renewal pays. */
    paidCap: number | null;
    activeSubscriptions: number;
    cancelledSubscriptions: number;
    renewals: number;
    paidRenewals: number;
    earnings: number;
    lastRenewalAt: string | null;
  } | null;
}

export interface ProgramSettings {
  commissionType: "PERCENTAGE" | "FIXED";
  commissionValue: number;
  discountValue: number;
  minimumPayoutAmount: number;
  automaticApproval: boolean;
  joinText?: string;
  joinTitle?: string;
  ambassadorText?: string;
  attributionMethod?: "STATIC_CODE" | "SNEAKYLINK";
  sneakylinkCodeLifetime?: number;
  sneakylinkCookieWindow?: number;
  name?: string;
}

/** The three pieces of copy shown on a brand's join page. */
export interface JoinContentFields {
  joinTitle: string | null;
  joinText: string | null;
  ambassadorText: string | null;
}

export interface ProgramJoinContent {
  programId: string;
  isDefault: boolean;
  /** This program's own copy. A null field means it inherits the brand default. */
  overrides: JoinContentFields;
  /** The brand-wide baseline, edited in Settings → Appearance. */
  brandDefaults: JoinContentFields;
  /** What affiliates actually see: overrides applied on top of brandDefaults. */
  effective: JoinContentFields;
}

export interface ProgramApplication {
  /** Affiliate id — the row links through to the affiliate profile. */
  id: string;
  /**
   * The application itself. Approve/reject key off this, not `id`: one
   * affiliate can hold applications to several programs and those are separate
   * decisions.
   */
  applicationId: string;
  firstName: string;
  lastName: string;
  username: string;
  avatar: string;
  status: "PENDING" | "APPROVED" | "REJECTED" | "ACTIVE";
  email: string;
  /** Null for sneakylink programs — codes are minted per click, not at apply time. */
  code: string | null;
  programId: string;
  programName: string;
  attributionMethod?: "STATIC_CODE" | "SNEAKYLINK";
  socialMedias: Array<{
    socialMedia: string;
    username: string;
  }>;
  createdAt: string;
}

/** A pending applicant on one program's detail page. */
export interface ProgramApplicant {
  id: string;
  applicationId: string;
  email: string;
  firstName: string;
  lastName: string;
  code: string | null;
  status: string;
  createdAt: string;
}

export interface JoinProgramResponse {
  success: boolean;
  // Matches the backend payload: `{ success, join, apply, data }`. This used to
  // be declared as `joined`, which was always undefined on the wire.
  join: boolean;
  apply: boolean;
  data: {
    affiliateId: string;
    programId: string;
    totalSales: number;
    totalCommissions: number;
    totalOrders: number;
    conversionRate: number;
    lastOrderDate: string | null;
    createdAt: string;
    updatedAt: string;
    code: string;
    status: "APPROVED" | "PENDING" | "REJECTED" | "ACTIVE";
  };
}

export interface PaginatedProgramResponse {
  data: ProgramDetails[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/** A program as described in the switch-programs comparison. */
export interface SwitchProgramSummary {
  id: string;
  name: string;
  isDefault: boolean;
  commissionType: "PERCENTAGE" | "FIXED";
  commissionValue: string;
  discountValue: string;
  attributionMethod: "STATIC_CODE" | "SNEAKYLINK";
  /** false → applying doesn't move them until the brand approves. */
  automaticApproval: boolean;
  /** Present only for the program they're currently in. */
  code?: string | null;
  unpaidBalance?: number;
}

interface JoinStatus {
  /** In *this* program. */
  joined: boolean;
  pendingApplication: boolean;
  /**
   * Set when the affiliate is in a different program of this brand. An
   * affiliate belongs to one program per brand, so joining moves them — show
   * the comparison and send `confirmSwitch` once they agree.
   */
  switchFrom?: SwitchProgramSummary | null;
  target?: SwitchProgramSummary | null;
  /** Set when the move is blocked until the brand pays what it owes. */
  pendingPayout?: { amount: number } | null;
}

/** One PENDING payout the brand has to settle before removing its affiliate. */
export interface RemovalOutstandingPayout {
  id: string;
  amount: number;
  createdAt: string;
  /** Payment methods this payout can be settled with (affiliate ∩ brand). */
  matchingPaymentInfos: MatchingPaymentInfo[];
}

export interface RemovalOutstandingAffiliate {
  affiliateId: string;
  affiliate: {
    firstName: string | null;
    lastName: string | null;
    email: string;
    profileImage: string | null;
  };
  code: string | null;
  payouts: RemovalOutstandingPayout[];
  /** Sum of `payouts[].amount`. */
  total: number;
}

export interface RemovalUnrequestedBalance {
  affiliateId: string;
  affiliate: { firstName: string | null; lastName: string | null; email: string };
  amount: number;
}

/**
 * What removing a set of affiliates would run into. `blocked` means at least
 * one PENDING payout exists and the backend will refuse (409 PENDING_PAYOUT)
 * until it is settled through `payoutService.bulkPayout`.
 */
export interface RemovalPreflight {
  success: boolean;
  blocked: boolean;
  affiliateCount: number;
  enrolledCount: number;
  outstanding: RemovalOutstandingAffiliate[];
  totalOutstanding: number;
  /** Earned but never requested — nothing can pay it out after removal. */
  unrequestedBalances: RemovalUnrequestedBalance[];
  totalUnrequested: number;
}

/** Body of the 409 the removal endpoints return while payouts are owed. */
export interface PendingPayoutConflict {
  success: false;
  code: "PENDING_PAYOUT";
  message: string;
  data: { outstanding: RemovalOutstandingAffiliate[]; totalOutstanding: number };
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface FindAffiliatesParams {
  page: number;
  limit: number;
  status?: "ACTIVE" | "INACTIVE" | "PENDING";
  searchBy?: "NAME" | "EMAIL" | "CODE";
  maxRevenue?: number;
  minRevenue?: number;
  minAge?: number;
  maxAge?: number;
  includeNoAge?: boolean;
  countries?: string;
  gender?: "MALE" | "FEMALE" | "OTHER";
  niches?: string;
  keyword?: string;
  sortBy?: "NAME" | "EMAIL" | "REVENUE";
  sortOrder?: "ASC" | "DESC";
}

export interface GetApplicationsParams {
  page?: number;
  limit?: number;
  searchBy?: "NAME" | "EMAIL" | "CODE";
  maxRevenue?: number;
  minRevenue?: number;
  minAge?: number;
  maxAge?: number;
  countries?: string;
  gender?: "MALE" | "FEMALE" | "OTHER";
  niches?: string;
  keyword?: string;
}

export const programService = {
  /** Affiliate Programs > Analytics for one period (7, 30 or 90 days). */
  async getProgramsAnalytics(days: number): Promise<ProgramsAnalytics> {
    const response = await brandApi.get<ProgramsAnalytics>("/analytics/brand/programs", {
      params: { days },
    });
    return response.data;
  },

  // create program: brand
  async createProgram(
    settings: Omit<CreateProgramPayload, "name" | "description">
  ) {
    const response = await brandApi.post("/program/create", {
      name: "Default Program",
      description: "Default Program Description",
      ...settings,
    });
    return response.data;
  },

  // create additional program (non-default)
  async createAdditionalProgram(settings: ProgramSettings) {
    const response = await brandApi.post("/program/create-additional", settings);
    return response.data;
  },

  // find active programs : joined by affiliate
  async findActivePrograms() {
    const response = await affiliateApi.get<ProgramDetails[]>(
      "/program/find-active-programs"
    );
    return response.data;
  },

  async generateCode(programId: string): Promise<string> {
    try {
      const { data } = await affiliateApi.get(
        `/program/generate-code/${programId}`
      );
      return data;
    } catch (error) {
      console.error("Error generating code:", error);
      throw error;
    }
  },

  async checkCodeAvailability(code: string, programId: string) {
    try {
      const { data } = await affiliateApi.get(
        `/program/check-code-availability/${code}/${programId}`
      );
      return data;
    } catch (error) {
      console.error("Error checking code:", error);
      throw error;
    }
  },

  /**
   * @param programToken share token of the program being joined (`?p=` in the
   *   join link). Omitted means the brand's default program.
   * @param confirmSwitch the affiliate has seen what they're leaving behind and
   *   agreed to move. Required when they're already in another program of this
   *   brand — without it the API responds 409 `SWITCH_REQUIRED` carrying both
   *   programs so the caller can show the comparison.
   */
  async applyToProgram(
    code: string,
    username: string,
    programToken?: string,
    confirmSwitch?: boolean,
    adRequestToken?: string
  ): Promise<JoinProgramResponse> {
    try {
      const { data } = await affiliateApi.post("/program/join-or-apply", {
        username,
        code,
        ...(programToken ? { programToken } : {}),
        ...(confirmSwitch ? { confirmSwitch: true } : {}),
        ...(adRequestToken ? { adRequestToken } : {}),
      });
      return data;
    } catch (error) {
      console.error("Error joining program:", error);
      throw error;
    }
  },

  async getActiveAffiliates(): Promise<Affiliate[]> {
    const response = await brandApi.get("/program/affiliates/APPROVED");
    return response.data;
  },

  async inviteAffiliate(email: string, message?: string) {
    const response = await brandApi.post("/program/invite-affiliate", {
      email,
      message,
    });
    return response.data;
  },

  async getApplications(
    params: GetApplicationsParams = {}
  ): Promise<PaginatedResponse<ProgramApplication>> {
    // Only include parameters that have been explicitly set
    const queryParams: Record<string, string | number | undefined> = {
      page: params.page || 1,
      limit: params.limit || 10,
    };

    // Only add searchBy if it's explicitly provided
    if (params.searchBy) {
      queryParams.searchBy = params.searchBy;
    }

    // Only add filter parameters if they are explicitly set
    if (params.maxRevenue !== undefined)
      queryParams.maxRevenue = params.maxRevenue;
    if (params.minRevenue !== undefined)
      queryParams.minRevenue = params.minRevenue;
    if (params.minAge !== undefined) queryParams.minAge = params.minAge;
    if (params.maxAge !== undefined) queryParams.maxAge = params.maxAge;
    if (params.countries) queryParams.countries = params.countries;
    if (params.gender) queryParams.gender = params.gender;
    if (params.niches) queryParams.niches = params.niches;
    if (params.keyword) queryParams.keyword = params.keyword;

    const response = await brandApi.get("/program/applications", {
      params: queryParams,
    });
    return response.data;
  },

  /**
   * @param scope "BRAND" (default) asks whether the affiliate works with this
   *   brand at all — right for the brand's own page. "PROGRAM" asks about this
   *   one program, which is what a share link needs: already being in the
   *   default program must not read as already-joined for a shared program.
   */
  async checkIfJoined(programId: string): Promise<JoinStatus> {
    const { data } = await affiliateApi.get<JoinStatus>(
      `/program/affiliate/check-join-status/${programId}`
    );
    return {
      joined: data.joined,
      pendingApplication: data.pendingApplication,
      switchFrom: data.switchFrom ?? null,
      target: data.target ?? null,
      pendingPayout: data.pendingPayout ?? null,
    };
  },

  async getProgram() {
    const { data } = await brandApi.get("/program/find-my-program");
    return data;
  },

  async getProgramById(programId: string) {
    const { data } = await brandApi.get(`/program/${programId}`);
    return data;
  },

  async getMyPrograms() {
    const { data } = await brandApi.get("/program/my-programs");
    return data;
  },

  // Join page copy for one program: its own overrides plus the brand defaults it
  // falls back to. `overrides.*` being null means "inheriting".
  async getJoinContent(programId: string): Promise<ProgramJoinContent> {
    const { data } = await brandApi.get<ProgramJoinContent>(
      `/program/${programId}/join-content`
    );
    return data;
  },

  // Send null for a field to clear the override and inherit again; omit a field
  // to leave it untouched.
  async updateJoinContent(
    programId: string,
    content: Partial<JoinContentFields>
  ) {
    const { data } = await brandApi.patch("/program/join-content", {
      programId,
      ...content,
    });
    return data;
  },

  async getAffiliateCountsForPrograms() {
    const { data } = await brandApi.get<Record<string, number>>("/program/affiliate-counts");
    return data;
  },

  async getMyCustomPrograms() {
    const { data } = await brandApi.get("/program/my-custom-programs");
    return data;
  },

  async updateProgram(settings: ProgramSettings & { programId: string }) {
    return brandApi.patch<Program>("/program/update", settings);
  },

  async updateProgramRenewals(
    programId: string,
    settings: ProgramRenewalSettings
  ): Promise<ProgramRenewalSettings & { id: string }> {
    const { data } = await brandApi.patch<
      ProgramRenewalSettings & { id: string; renewalRate: number | string }
    >(`/program/${programId}/renewals`, settings);
    return { ...data, renewalRate: Number(data.renewalRate) };
  },

  // Brand tiers on one program (brand side). PUT replaces the ladder.
  async getProgramTiers(programId: string): Promise<ProgramTier[]> {
    const { data } = await brandApi.get<ProgramTier[]>(`/program/${programId}/tiers`);
    return data;
  },

  async updateProgramTiers(
    programId: string,
    tiers: Omit<ProgramTier, "id">[]
  ): Promise<ProgramTier[]> {
    const { data } = await brandApi.put<ProgramTier[]>(
      `/program/${programId}/tiers`,
      { tiers }
    );
    return data;
  },

  // The dashboard hub for one program the affiliate is enrolled in.
  async getAffiliateHub(programId: string): Promise<AffiliateProgramHub> {
    const { data } = await affiliateApi.get<AffiliateProgramHub>(
      `/program/affiliate/hub/${programId}`
    );
    return data;
  },

  async setDefaultProgram(programId: string) {
    const { data } = await brandApi.patch("/program/set-default", { programId });
    return data;
  },

  // search programs with pagination
  async searchPrograms(
    keyword: string,
    page: number = 1,
    limit: number = 20,
    filterBy?: "ORDERS" | "RANK" | "AFFILIATE",
    sortOrderBy: "ASC" | "DESC" = "DESC",
    accountType: "TEST" | "REAL" | "ALL" = "REAL"
  ): Promise<PaginatedProgramResponse> {
    return affiliateApi
      .get("/program/search-brands", {
        params: {
          keyword,
          page: page,
          limit,
          filterBy,
          sortOrderBy,
          accountType,
        },
      })
      .then((response) => response.data);
  },

  async approveApplications(applicationIds: string[]): Promise<void> {
    try {
      await brandApi.post("/program/approve-applications", {
        applicationIds,
      });
    } catch (error) {
      console.error("Error approving applications:", error);
      throw error;
    }
  },

  async rejectApplications(applicationIds: string[]): Promise<void> {
    try {
      await brandApi.post("/program/reject-applications", {
        applicationIds,
      });
    } catch (error) {
      console.error("Error rejecting applications:", error);
      throw error;
    }
  },

  async findAffiliates(
    params: Partial<FindAffiliatesParams> = {}
  ): Promise<PaginatedResponse<Affiliate>> {
    // Always use the /program/affiliates/APPROVED endpoint regardless of filter or search parameters
    // Only include parameters that have been explicitly set
    const queryParams: Record<string, string | number> = {
      page: params.page || 1,
      limit: params.limit || 10,
    };

    // Only add searchBy if it's explicitly provided
    if (params.searchBy) {
      queryParams.searchBy = params.searchBy;
    }

    // Only add filter parameters if they are explicitly set
    if (params.maxRevenue !== undefined)
      queryParams.maxRevenue = params.maxRevenue;
    if (params.minRevenue !== undefined)
      queryParams.minRevenue = params.minRevenue;
    if (params.minAge !== undefined) queryParams.minAge = params.minAge;
    if (params.maxAge !== undefined) queryParams.maxAge = params.maxAge;
    if (params.includeNoAge !== undefined)
      queryParams.includeNoAge = String(params.includeNoAge);
    if (params.countries) queryParams.countries = params.countries;
    if (params.gender) queryParams.gender = params.gender;
    if (params.niches) queryParams.niches = params.niches;
    if (params.keyword) queryParams.keyword = params.keyword;

    // Sorting (handled server-side so it works across pages)
    if (params.sortBy) {
      queryParams.sortBy = params.sortBy;
      queryParams.sortOrder = params.sortOrder || "ASC";
    }

    const response = await brandApi.get("/program/affiliates/APPROVED", {
      params: queryParams,
    });

    return response.data;
  },

  // List users enrolled in a given program (brand context)
  async listUsersInProgram(programId: string, page: number = 1, limit: number = 10, keyword?: string) {
    const { data } = await brandApi.get(`/program/${programId}/users`, {
      params: { page, limit, ...(keyword ? { keyword } : {}) },
    });
    return data;
  },

  // Issue a fresh share token, invalidating this program's old join link.
  // Affiliates who already joined through the old link keep their enrollment.
  async rotateShareToken(
    programId: string
  ): Promise<{ success: boolean; programId: string; shareToken: string }> {
    const { data } = await brandApi.post(
      `/program/${programId}/rotate-share-token`
    );
    return data;
  },

  // Pending applicants on one program. Only manual-approval programs produce
  // these — which is every program created alongside a share link.
  async listApplicantsInProgram(
    programId: string,
    page: number = 1,
    limit: number = 10,
    keyword?: string
  ): Promise<PaginatedResponse<ProgramApplicant>> {
    const { data } = await brandApi.get<PaginatedResponse<ProgramApplicant>>(
      `/program/${programId}/applicants`,
      { params: { page, limit, ...(keyword ? { keyword } : {}) } }
    );
    return data;
  },

  // Get available affiliates from other programs (for assigning to current program)
  async getAvailableAffiliates(programId: string, page: number = 1, limit: number = 10, keyword?: string) {
    const { data } = await brandApi.get(`/program/${programId}/available-affiliates`, {
      params: { page, limit, ...(keyword ? { keyword } : {}) },
    });
    return data;
  },

  // Add an existing affiliate (enrolled in another program of the same brand) into target program
  async addAffiliateToProgram(programId: string, affiliateId: string) {
    const { data } = await brandApi.post(`/program/${programId}/add-user/${affiliateId}`);
    return data;
  },

  // Bulk-add existing affiliates into a program
  async bulkAddAffiliatesToProgram(
    programId: string,
    affiliateIds: string[],
  ): Promise<{
    success: boolean;
    results: { affiliateId: string; success: boolean; error?: string }[];
  }> {
    const { data } = await brandApi.post(`/program/${programId}/bulk-add-affiliates`, {
      affiliateIds,
    });
    return data;
  },

  // What removing these affiliates would run into (pending payouts to settle
  // first, unrequested balances) — ask before the confirm dialog commits.
  async getAffiliateRemovalPreflight(
    programId: string,
    affiliateIds: string[],
  ): Promise<RemovalPreflight> {
    const { data } = await brandApi.post(
      `/program/${programId}/removal-preflight`,
      { affiliateIds },
    );
    return data;
  },

  // Remove an affiliate from a specific program (also deletes the Shopify
  // discount code). 409 PENDING_PAYOUT while the brand still owes a payout.
  async removeAffiliateFromProgram(
    programId: string,
    affiliateId: string,
  ): Promise<{ success: boolean; removed: boolean; archived: boolean }> {
    const { data } = await brandApi.delete(
      `/program/${programId}/affiliates/${affiliateId}`,
    );
    return data;
  },

  // Bulk-remove affiliates from a specific program. Refused as a whole (409
  // PENDING_PAYOUT) when any of them still has a pending payout.
  async bulkRemoveAffiliatesFromProgram(
    programId: string,
    affiliateIds: string[],
  ): Promise<{
    success: boolean;
    results: {
      affiliateId: string;
      success: boolean;
      archived?: boolean;
      error?: string;
    }[];
  }> {
    const { data } = await brandApi.post(`/program/${programId}/bulk-remove-affiliates`, {
      affiliateIds,
    });
    return data;
  },

  // Check if an email has a pending invitation (public endpoint)
  async checkPendingInvitation(programId: string, email: string): Promise<{
    success: boolean;
    hasPendingInvitation: boolean;
    invitation?: {
      id: string;
      email: string;
      expiresAt: string;
    } | null;
  }> {
    try {
      const response = await affiliateApi.get(`/program/check-invitation/${programId}`, {
        params: { email },
      });
      return response.data;
    } catch (error) {
      console.error("Error checking pending invitation:", error);
      return {
        success: false,
        hasPendingInvitation: false,
      };
    }
  },
};
